<template>
  <div class="shop-profile">
    <!-- 审核状态提示：商家提交后需平台审核通过才在商城展示 -->
    <div class="audit-banner" :class="auditClass">
      <span class="audit-icon">{{ auditIcon }}</span>
      <span class="audit-text">{{ auditText }}</span>
      <span class="audit-remark" v-if="auditRemark">（{{ auditRemark }}）</span>
    </div>

    <div class="panel-card">
      <div class="panel-title">🏪 店铺资料</div>
      <p class="panel-tip">
        店铺资料会展示在商城的「店铺主页」。修改后需平台审核通过才会更新到商城（审核期间商城仍展示上一次已通过的资料）。
      </p>

      <div class="form-grid">
        <div class="form-group full">
          <label>商家类型 <span class="required">*</span></label>
          <div class="type-radio">
            <label class="type-item" :class="{ active: form.merchant_type === 1 }">
              <input type="radio" :value="1" v-model.number="form.merchant_type">
              <span class="type-name">个人商家</span>
              <span class="type-desc">无营业执照，需实名认证（姓名 + 身份证）</span>
            </label>
            <label class="type-item" :class="{ active: form.merchant_type === 2 }">
              <input type="radio" :value="2" v-model.number="form.merchant_type">
              <span class="type-name">企业商家</span>
              <span class="type-desc">需上传营业执照等企业资质</span>
            </label>
          </div>
        </div>

        <div class="form-group">
          <label>店铺名称 <span class="required">*</span></label>
          <input v-model.trim="form.shop_name" placeholder="如：糖果服饰旗舰店">
        </div>
        <div class="form-group">
          <label>店铺 Logo</label>
          <div class="image-row">
            <input v-model.trim="form.shop_logo" placeholder="图片URL">
            <button type="button" class="picker-btn" @click="pickImage('shop_logo')">上传</button>
            <img v-if="form.shop_logo" :src="form.shop_logo" class="thumb" alt="">
          </div>
        </div>
        <div class="form-group full">
          <label>店铺简介</label>
          <textarea v-model.trim="form.shop_desc" rows="2" placeholder="一句话介绍店铺"></textarea>
        </div>
        <div class="form-group">
          <label>联系方式 <span class="required" v-if="isPersonal">*</span></label>
          <input v-model.trim="form.contact" maxlength="50" placeholder="手机号 / 微信号（商城资质页展示时自动脱敏）">
        </div>
      </div>

      <p class="current-tip" v-if="shop.audit_status === 0 && shop.shop_name">
        当前商城展示：<b>{{ shop.shop_name }}</b>（新资料审核通过后自动替换）
      </p>
    </div>

    <div class="panel-card">
      <div class="panel-title">🧾 {{ isPersonal ? '个人资质（实名认证）' : '企业资质（营业执照）' }}</div>
      <p class="panel-tip">
        资质信息仅在商城「店铺主页 → 查看商家资质」中展示，且买家需通过安全验证后才能查看（证件号展示时自动脱敏）。
        营业执照 / 身份证等证件图片保存在私有目录，不会进入「图片空间」（图片空间按店铺隔离，仅显示本店铺图片）。
      </p>

      <div class="form-grid">
        <template v-if="isPersonal">
          <div class="form-group">
            <label>真实姓名 <span class="required">*</span></label>
            <input v-model.trim="form.subject_name" placeholder="与身份证一致的姓名">
          </div>
          <div class="form-group">
            <label>身份证号 <span class="required">*</span></label>
            <input v-model.trim="form.legal_id_no" maxlength="32" placeholder="身份证号码">
          </div>
          <div class="form-group full">
            <label>经营地址（省 / 市 / 区） <span class="required">*</span></label>
            <div class="image-row">
              <input v-model="regionText" readonly placeholder="请选择经营地址（省 / 市 / 区）" @click="openRegionPicker">
              <button type="button" class="picker-btn" @click="openRegionPicker">选择地区</button>
            </div>
          </div>
          <div class="form-group">
            <label>身份证正面（人像面） <span class="required">*</span></label>
            <div class="image-row">
              <input v-model.trim="form.legal_id_front" placeholder="图片URL">
              <button type="button" class="picker-btn" @click="pickImage('legal_id_front')">上传</button>
              <img v-if="form.legal_id_front" :src="form.legal_id_front" class="thumb" alt="">
            </div>
          </div>
          <div class="form-group">
            <label>身份证反面（国徽面） <span class="required">*</span></label>
            <div class="image-row">
              <input v-model.trim="form.legal_id_back" placeholder="图片URL">
              <button type="button" class="picker-btn" @click="pickImage('legal_id_back')">上传</button>
              <img v-if="form.legal_id_back" :src="form.legal_id_back" class="thumb" alt="">
            </div>
          </div>
        </template>

        <template v-else>
          <div class="form-group">
            <label>企业 / 个体户名称 <span class="required">*</span></label>
            <input v-model.trim="form.subject_name" placeholder="如：深圳市糖果服饰有限公司">
          </div>
          <div class="form-group">
            <label>统一社会信用代码</label>
            <input v-model.trim="form.credit_code" placeholder="如：91440300MA5XXXXXXX">
          </div>
          <div class="form-group full">
            <label>营业执照图片 <span class="required">*</span></label>
            <div class="image-row">
              <input v-model.trim="form.license_image" placeholder="营业执照图片URL">
              <button type="button" class="picker-btn" @click="pickImage('license_image')">上传</button>
              <img v-if="form.license_image" :src="form.license_image" class="thumb" alt="">
            </div>
          </div>
          <div class="form-group">
            <label>类型</label>
            <input v-model.trim="form.company_type" maxlength="50" placeholder="如：有限责任公司 / 个体工商户">
          </div>
          <div class="form-group">
            <label>营业期限</label>
            <input v-model.trim="form.biz_term" maxlength="50" placeholder="如：2020-01-01 至 长期">
          </div>
          <div class="form-group full">
            <label>住所（省 / 市 / 区） <span class="required">*</span></label>
            <div class="image-row">
              <input v-model="regionText" readonly placeholder="请选择住所所在地区（省 / 市 / 区）" @click="openRegionPicker">
              <button type="button" class="picker-btn" @click="openRegionPicker">选择地区</button>
            </div>
          </div>
          <div class="form-group full">
            <label>住所详细地址</label>
            <input v-model.trim="form.biz_detail" maxlength="255" placeholder="如：科技园南路 8 号 3 栋 501">
          </div>
          <div class="form-group">
            <label>法定代表人姓名</label>
            <input v-model.trim="form.legal_name" placeholder="法人姓名">
          </div>
          <div class="form-group">
            <label>法定代表人证件号</label>
            <input v-model.trim="form.legal_id_no" maxlength="32" placeholder="身份证号（展示时自动脱敏）">
          </div>
          <div class="form-group">
            <label>法人证件（人像面）</label>
            <div class="image-row">
              <input v-model.trim="form.legal_id_front" placeholder="图片URL">
              <button type="button" class="picker-btn" @click="pickImage('legal_id_front')">上传</button>
              <img v-if="form.legal_id_front" :src="form.legal_id_front" class="thumb" alt="">
            </div>
          </div>
          <div class="form-group">
            <label>法人证件（国徽面）</label>
            <div class="image-row">
              <input v-model.trim="form.legal_id_back" placeholder="图片URL">
              <button type="button" class="picker-btn" @click="pickImage('legal_id_back')">上传</button>
              <img v-if="form.legal_id_back" :src="form.legal_id_back" class="thumb" alt="">
            </div>
          </div>
          <div class="form-group full">
            <label>经营范围</label>
            <textarea v-model.trim="form.business_scope" rows="2" maxlength="500" placeholder="如：服装服饰批发与零售"></textarea>
          </div>
        </template>
      </div>

      <div class="form-group full" style="margin-top: 14px;">
        <label>其他资质图片（可选，如品牌授权书）</label>
        <div class="image-row">
          <input v-model.trim="form.other_image" placeholder="图片URL">
          <button type="button" class="picker-btn" @click="pickImage('other_image')">上传</button>
          <img v-if="form.other_image" :src="form.other_image" class="thumb" alt="">
        </div>
      </div>
      <div class="form-group full">
        <label>其他资质说明</label>
        <textarea v-model.trim="form.other_desc" rows="2" placeholder="如：品牌授权有效期至 2027-12-31"></textarea>
      </div>

      <div class="panel-actions">
        <button class="panel-btn" :disabled="loading" @click="load">{{ loading ? '加载中...' : '重新加载' }}</button>
        <button class="panel-btn confirm" :disabled="saving" @click="save">{{ saving ? '提交中...' : '提交审核' }}</button>
      </div>
    </div>

    <!-- 省市区选择（经营地址 / 企业住所） -->
    <van-popup v-model="regionVisible" round position="bottom">
      <van-cascader
        title="请选择所在地区"
        :options="regionOptions"
        v-model="cascaderValue"
        @close="regionVisible = false"
        @finish="onRegionFinish"
      />
    </van-popup>

    <input ref="fileInput" type="file" accept="image/*" style="display: none;" @change="onFileChange">
  </div>
</template>

<script>
import { getMerchantInfoApi, getMyQualificationApi, saveQualificationApi } from '@/api/merchant'
import { uploadImageLibraryApi, uploadQualificationImageApi } from '@/api/admin'

// 允许进入「图片空间」的表单字段：仅店铺 Logo（便于商品管理时复用）
// 营业执照 / 身份证正反面 / 其他资质图走私有上传（/api/upload/qualimage），不写入图片空间
const LIBRARY_IMAGE_FIELDS = ['shop_logo']

/**
 * 店铺资料 / 商家资质维护（商家后台「店铺资料」Tab）
 * 说明：
 *   - 支持个人商家（实名认证）与企业商家（营业执照）；
 *   - 提交后状态为「待审核」，需超级管理员审核通过后才在商城展示；
 *   - 超级管理员账号没有店铺资料，此页面仅普通商家可见。
 */
export default {
  name: 'ShopProfile',
  data () {
    return {
      loading: false,
      saving: false,
      // 当前正在上传图片的表单字段
      imageTarget: '',
      // 已生效的店铺资料与审核状态
      shop: {
        shop_name: '',
        audit_status: 1,
        audit_remark: '',
        pending: {}
      },
      // 资质审核状态
      qualAudit: { audit_status: 0, audit_remark: '' },
      // 省市区选择（经营地址 / 企业住所）
      regionVisible: false,
      cascaderValue: '',
      regionText: '',
      form: {
        merchant_type: 2,
        shop_name: '',
        shop_logo: '',
        shop_desc: '',
        contact: '',
        subject_name: '',
        credit_code: '',
        license_image: '',
        legal_name: '',
        legal_id_no: '',
        legal_id_front: '',
        legal_id_back: '',
        biz_province: '',
        biz_city: '',
        biz_district: '',
        biz_detail: '',
        company_type: '',
        biz_term: '',
        business_scope: '',
        other_image: '',
        other_desc: ''
      }
    }
  },
  computed: {
    isPersonal () {
      return Number(this.form.merchant_type) === 1
    },
    // 全国省市区（级联选择数据，复用收货地址的省市区接口）
    regionOptions () {
      return this.$store.getters['address/nationlAddressList']
    },
    // 店铺资料审核状态：0待审核 1已通过 2已驳回
    auditClass () {
      const status = Number(this.shop.audit_status)
      if (status === 0) return 'pending'
      if (status === 2) return 'rejected'
      return 'passed'
    },
    auditIcon () {
      const status = Number(this.shop.audit_status)
      if (status === 0) return '⏳'
      if (status === 2) return '⚠️'
      return '✅'
    },
    auditText () {
      const status = Number(this.shop.audit_status)
      if (status === 0) return '店铺资料已提交，等待平台审核（审核通过后在商城展示）'
      if (status === 2) return '店铺资料未通过审核，请按驳回原因修改后重新提交'
      return '店铺资料已通过审核，已在商城展示'
    },
    auditRemark () {
      return Number(this.shop.audit_status) === 2 ? (this.shop.audit_remark || '') : ''
    }
  },
  created () {
    // 首次进入时拉取省市区数据（级联选择用）
    if (Object.keys(this.$store.state.address.regionData).length === 0) {
      this.$store.dispatch('address/getAddressSource')
    }
    this.load()
  },
  methods: {
    // 打开省市区选择（经营地址 / 企业住所）
    openRegionPicker () {
      if (Object.keys(this.$store.state.address.regionData).length === 0) {
        this.$store.dispatch('address/getAddressSource')
      }
      this.regionVisible = true
    },
    onRegionFinish ({ selectedOptions }) {
      this.form.biz_province = (selectedOptions[0] && selectedOptions[0].text) || ''
      this.form.biz_city = (selectedOptions[1] && selectedOptions[1].text) || ''
      this.form.biz_district = (selectedOptions[2] && selectedOptions[2].text) || ''
      this.regionText = selectedOptions.map(opt => opt.text).join('/')
      this.cascaderValue = selectedOptions.length ? String(selectedOptions[selectedOptions.length - 1].value) : ''
      this.regionVisible = false
    },
    async load () {
      this.loading = true
      try {
        const infoRes = await getMerchantInfoApi()
        const info = (infoRes.data || {})

        const qualRes = await getMyQualificationApi()
        const data = (qualRes.data || {})
        const shop = data.shop || {}
        const q = data.qualification || {}

        this.shop = {
          shop_name: shop.shop_name || info.shop_name || '',
          audit_status: shop.audit_status !== undefined ? Number(shop.audit_status) : 1,
          audit_remark: shop.audit_remark || '',
          pending: shop.pending || {}
        }
        this.qualAudit = {
          audit_status: q.audit_status !== undefined ? Number(q.audit_status) : 0,
          audit_remark: q.audit_remark || ''
        }

        // 待审核数据优先回显（商家刚提交的内容），否则回显已生效资料
        const pending = shop.pending || {}
        const merchantType = Number(shop.merchant_type || info.merchant_type || 2) === 1 ? 1 : 2
        this.form = {
          merchant_type: merchantType,
          shop_name: pending.shop_name || shop.shop_name || info.shop_name || '',
          shop_logo: pending.shop_logo || shop.shop_logo || info.shop_logo || '',
          shop_desc: pending.shop_desc || shop.shop_desc || info.shop_desc || '',
          contact: shop.contact || info.contact || '',
          subject_name: q.subject_name || '',
          credit_code: q.credit_code || '',
          license_image: q.license_image || '',
          legal_name: q.legal_name || '',
          legal_id_no: q.legal_id_no || '',
          legal_id_front: q.legal_id_front || '',
          legal_id_back: q.legal_id_back || '',
          biz_province: q.biz_province || '',
          biz_city: q.biz_city || '',
          biz_district: q.biz_district || '',
          biz_detail: q.biz_detail || '',
          company_type: q.company_type || '',
          biz_term: q.biz_term || '',
          business_scope: q.business_scope || '',
          other_image: q.other_image || '',
          other_desc: q.other_desc || ''
        }
        // 省市区回显
        this.regionText = [q.biz_province, q.biz_city, q.biz_district].filter(Boolean).join('/')
      } catch (e) {
        // 错误提示由请求拦截器处理
      } finally {
        this.loading = false
      }
    },
    pickImage (field) {
      this.imageTarget = field
      const input = this.$refs.fileInput
      if (input) input.click()
    },
    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      const target = this.imageTarget
      this.imageTarget = ''
      if (!file || !target) return
      try {
        // 营业执照 / 身份证等资质证件图：私有上传（不进入图片空间，避免跨商家共享）
        const api = LIBRARY_IMAGE_FIELDS.indexOf(target) > -1
          ? uploadImageLibraryApi
          : uploadQualificationImageApi
        const { data } = await api(file)
        const url = (data && data.url) || ''
        if (url) {
          this.form[target] = url
        } else {
          alert('图片上传失败，请重试')
        }
      } catch (err) {
        alert('图片上传失败，请重试')
      }
    },
    async save () {
      const f = this.form
      const isPersonal = Number(f.merchant_type) === 1
      if (!f.shop_name) { alert('请填写店铺名称'); return }
      if (isPersonal) {
        if (!f.subject_name) { alert('请填写真实姓名'); return }
        if (!f.legal_id_no) { alert('请填写身份证号'); return }
        if (!f.contact) { alert('请填写联系方式'); return }
        // 省 / 市必填；区县选填（部分城市如东莞、中山没有区县层级）
        if (!f.biz_province || !f.biz_city) { alert('请选择经营地址（省 / 市）'); return }
        if (!f.legal_id_front || !f.legal_id_back) { alert('请上传身份证正面与反面照片'); return }
      } else {
        if (!f.subject_name) { alert('请填写企业 / 个体户名称'); return }
        if (!f.license_image) { alert('请上传营业执照图片'); return }
        // 省 / 市必填；区县选填（部分城市如东莞、中山没有区县层级）
        if (!f.biz_province || !f.biz_city) { alert('请选择企业住所（省 / 市）'); return }
      }

      this.saving = true
      try {
        const { message } = await saveQualificationApi({ ...f })
        alert(message || '已提交，等待平台审核')
        await this.load()
      } catch (e) {
        // 错误提示由请求拦截器处理
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.shop-profile {
  // 审核状态提示条
  .audit-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 13px;
    margin-bottom: 16px;

    .audit-icon { font-size: 16px; }
    .audit-remark { color: inherit; opacity: 0.85; }

    &.pending { background: #fff7e6; color: #d46b08; border: 1px solid #ffe0a3; }
    &.rejected { background: #fff1f0; color: #cf1322; border: 1px solid #ffccc7; }
    &.passed { background: #f6ffed; color: #389e0d; border: 1px solid #b7eb8f; }
  }

  .panel-card {
    background: #fff;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  }

  .panel-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
  }

  .panel-tip {
    margin: 6px 0 14px;
    font-size: 12px;
    color: #98a2b3;
    line-height: 18px;
  }

  .current-tip {
    margin: 12px 0 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f5f7fa;
    font-size: 12px;
    color: #5b6673;

    b { color: #1f2d3d; }
  }

  // 商家类型选择
  .type-radio {
    display: flex;
    gap: 12px;

    .type-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 10px 14px;
      border: 1px solid #e3e8ef;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s;

      input { margin-right: 6px; }

      .type-name {
        font-size: 13px;
        font-weight: 600;
        color: #1f2d3d;
      }

      .type-desc {
        font-size: 12px;
        color: #98a2b3;
        line-height: 16px;
      }

      &.active {
        border-color: #ff9211;
        background: #fff8f2;
      }
    }
  }

  .form-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
  }

  .form-group {
    width: calc(50% - 7px);

    &.full {
      width: 100%;
    }

    label {
      display: block;
      font-size: 13px;
      color: #5b6673;
      margin-bottom: 6px;

      .required {
        color: #f04438;
      }
    }

    input,
    textarea {
      width: 100%;
      border: 1px solid #e3e8ef;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      font-family: inherit;
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: #ffb27a;
      }
    }

    textarea {
      resize: vertical;
    }
  }

  .image-row {
    display: flex;
    align-items: center;
    gap: 8px;

    input {
      flex: 1;
      min-width: 0;
    }

    .picker-btn {
      flex-shrink: 0;
      height: 34px;
      padding: 0 12px;
      border: 1px solid #ffd2b0;
      border-radius: 8px;
      background: #fff7f2;
      color: #ff6b35;
      font-size: 13px;
      cursor: pointer;
    }

    .thumb {
      width: 34px;
      height: 34px;
      object-fit: cover;
      border-radius: 6px;
      background: #f5f6f8;
      flex-shrink: 0;
    }
  }

  .panel-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;

    .panel-btn {
      height: 36px;
      padding: 0 20px;
      border: 1px solid #e3e8ef;
      border-radius: 8px;
      background: #fff;
      color: #5b6673;
      font-size: 13px;
      cursor: pointer;

      &.confirm {
        border: none;
        background: linear-gradient(90deg, #ecb53c, #ff9211);
        color: #fff;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}
</style>
